import { useQuery } from "@tanstack/react-query";
import { ArrowDownUp, Check, Clock, Timer, Truck } from "lucide-react";
import { useState } from "react";
import { useCompanyFormat } from "../../company";
import { etaText, progressOf } from "../../deliveries";
import {
  type BoardRow,
  clockOf,
  DEFAULT_LATE_MINUTES,
  dispatchSettingsQuery,
  doneNotes,
  dueOf,
  litresOf,
  type SegmentId,
  sinceOf,
} from "../../dispatch";
import { DELIVERY_STATUS } from "../../labels";
import { ageOf, kmBetween, type TruckPosition, truckPositionsQuery, truckState } from "../../tracking";
import styles from "./board.module.css";

type Group = { title: string; test: (r: BoardRow, lateAfter: number) => boolean };

/** Each tab's list (`Console / Dispatch / …`, left): its title, its order, and how it splits. */
const TABS: Record<
  SegmentId,
  { title: string; order: [string, string]; sortLabel: string; key: (r: BoardRow) => string; groups: Group[] }
> = {
  unassigned: {
    title: "To plan",
    order: ["Oldest first · select one to assign", "Latest first · select one to assign"],
    sortLabel: "By time",
    key: dueOf,
    groups: [
      { title: "Late", test: (r) => r.flags.late },
      { title: "Later today", test: () => true },
    ],
  },
  assigned: {
    title: "Waiting to start",
    order: ["Planned; the driver hasn't accepted yet", "Planned; latest start first"],
    sortLabel: "By start time",
    key: dueOf,
    groups: [
      { title: "Not accepted", test: (r) => r.flags.not_accepted },
      { title: "Next up", test: () => true },
    ],
  },
  in_progress: {
    title: "Under way",
    order: ["Longest at their step first", "Newest at their step first"],
    sortLabel: "By time at step",
    key: (r) => r.status_since,
    groups: [
      { title: "Needs a look", test: (r) => r.status === "suspended" || r.flags.slow },
      { title: "On the road", test: () => true },
    ],
  },
  done: {
    title: "Delivered",
    order: ["Newest first", "Oldest first"],
    sortLabel: "Newest first",
    key: (r) => r.status_since,
    groups: [
      { title: "Needs a look", test: (r, lateAfter) => doneNotes(r, lateAfter).issues.length > 0 },
      { title: "All good", test: () => true },
    ],
  },
  closed: {
    title: "Closed",
    order: ["Failed, cancelled or rerouted · newest first", "Failed, cancelled or rerouted · oldest first"],
    sortLabel: "Newest first",
    key: (r) => r.status_since,
    groups: [{ title: "Closed", test: () => true }],
  },
};
/** Done and closed read newest first; the rest oldest (or longest waiting) first. */
const NEWEST_FIRST = new Set<SegmentId>(["done", "closed"]);

/** The tab's deliveries as cards: select one to plan or follow it. */
export function Queue({
  segment,
  title,
  rows,
  selectedId,
  onSelect,
}: {
  segment: SegmentId;
  /** a heading other than the tab's, e.g. when a Needs attention chip is on */
  title?: string;
  rows: BoardRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const tab = TABS[segment];
  const [flipped, setFlipped] = useState(false);
  const positions = useQuery(truckPositionsQuery);
  const settings = useQuery(dispatchSettingsQuery);
  const lateAfter = settings.data?.late_after_minutes ?? DEFAULT_LATE_MINUTES;
  const desc = NEWEST_FIRST.has(segment) !== flipped;
  const sorted = [...rows].sort((a, b) => (desc ? -1 : 1) * tab.key(a).localeCompare(tab.key(b)));
  const seen = new Set<string>();
  const groups = tab.groups.map((g) => {
    const list = sorted.filter((r) => !seen.has(r.id) && g.test(r, lateAfter));
    for (const r of list) seen.add(r.id);
    return { title: g.title, list };
  });
  const positionOf = (r: BoardRow) =>
    r.assignment?.truck ? positions.data?.data.find((p) => p.truck_id === r.assignment?.truck?.id) : undefined;
  return (
    <>
      <header className={styles.queueHead}>
        <span className={styles.optionBody}>
          <h2 className={styles.queueTitle}>{title ?? tab.title}</h2>
          <span className={styles.small}>{tab.order[flipped ? 1 : 0]}</span>
        </span>
        <button
          type="button"
          className={styles.sortButton}
          aria-pressed={flipped}
          aria-label={`${tab.sortLabel}, ${tab.order[flipped ? 0 : 1].toLowerCase()} instead`}
          onClick={() => setFlipped((f) => !f)}
        >
          <ArrowDownUp size={14} aria-hidden /> {tab.sortLabel}
        </button>
      </header>
      <div className={styles.queueList}>
        {groups
          .filter((g) => g.list.length > 0)
          .map((g) => (
            <section key={g.title} aria-label={g.title}>
              <h3 className={styles.group}>{`${g.title} · ${g.list.length}`}</h3>
              <ul className={styles.cards}>
                {g.list.map((r) => (
                  <Card
                    key={r.id}
                    row={r}
                    segment={segment}
                    position={positionOf(r)}
                    lateAfter={lateAfter}
                    selected={r.id === selectedId}
                    onSelect={() => onSelect(r.id)}
                  />
                ))}
              </ul>
            </section>
          ))}
      </div>
    </>
  );
}

const firstName = (name: string | undefined) => name?.split(" ")[0] ?? "the driver";

/** A delivery as a card (`Console / Dispatch / …`, the list). */
function Card({
  row: r,
  segment,
  position: p,
  lateAfter,
  selected,
  onSelect,
}: {
  row: BoardRow;
  segment: SegmentId;
  position: TruckPosition | undefined;
  lateAfter: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const { timezone } = useCompanyFormat();
  const [label, tone] = DELIVERY_STATUS[r.status];
  const litres = litresOf(r);
  const waiting = r.status === "pending" || r.status === "fillsite_assigned";
  const alert = r.flags.late || r.flags.not_accepted || r.flags.slow;

  // when: the time it's due while it waits; how long at its step once planned; when it ended
  const due = r.scheduled_at ? clockOf(r.scheduled_at, timezone) : "Any time";
  const lateBy = r.flags.late ? ` · Late ${sinceOf(dueOf(r))}` : "";
  let when: { icon: typeof Clock; text: string; tone?: "warning" | "danger" | "success" };
  if (waiting) when = { icon: Clock, text: `${due}${lateBy}`, tone: r.flags.late ? "warning" : undefined };
  else if (segment === "done") when = { icon: Check, text: clockOf(r.status_since, timezone), tone: "success" };
  else if (segment === "closed") when = { icon: Clock, text: clockOf(r.status_since, timezone) };
  else if (r.status === "assigned" && !r.flags.not_accepted)
    when = { icon: Clock, text: r.scheduled_at ? `Starts ${due}` : "As soon as possible" };
  else
    when = {
      icon: Timer,
      text: sinceOf(r.status_since),
      tone: r.status === "suspended" ? "danger" : alert ? "warning" : undefined,
    };

  // where the truck is: km to go on the way, idle or moving otherwise
  const km = p && !p.stale && r.location ? kmBetween(p, r.location) : null;
  const where = !p
    ? null
    : p.stale
      ? `not seen for ${ageOf(p.recorded_at)}`
      : r.status === "in_transit" && r.eta
        ? etaText(r.eta, timezone).short
        : r.status === "in_transit" && km !== null
          ? `${km} km to go`
          : r.status === "at_customer" || r.status === "suspended"
            ? "at the customer"
            : truckState(p) === "moving"
              ? "moving"
              : p.moved_at
                ? `idle ${sinceOf(p.moved_at)}`
                : "idle";
  const crew = [r.assignment?.truck?.plate_number, r.assignment?.driver?.name, where].filter(Boolean).join(" · ");

  const note: [string, "warning" | "danger"] | null =
    r.status === "suspended"
      ? [r.hold_reason ?? "Held at the customer. Resume, reroute or close it.", "danger"]
      : r.flags.not_accepted
        ? [
            `Not accepted after ${sinceOf(r.status_since)}. Call ${firstName(r.assignment?.driver?.name)}, or reassign.`,
            "warning",
          ]
        : r.flags.slow
          ? [
              r.usual_minutes
                ? `Usually ${r.usual_minutes} min here`
                : `${sinceOf(r.status_since)} at this step, longer than the limit.`,
              "warning",
            ]
          : null;

  const chips: [string, string][] = [];
  if (waiting)
    chips.push(
      r.assignment?.fillsite
        ? [r.assignment.fillsite.name, "muted"]
        : litres
          ? ["No fill site yet", "warning"]
          : ["Parcel", "muted"],
    );
  // a credit order is billed on account: unpaid, but not waiting for payment
  if (r.order.on_credit && r.order.payment_status !== "paid") chips.push(["Credit", "info"]);
  else if (
    waiting &&
    !r.order.postpaid &&
    (r.order.payment_status === "unpaid" || r.order.payment_status === "pending")
  )
    chips.push(["Unpaid, waiting", "warning"]);
  if (r.order.postpaid && r.order.payment_status !== "paid") chips.push(["Postpaid", "info"]);
  if (r.dispatch_mode === "open") chips.push(["Offered to partners", "info"]);
  if (segment === "done") {
    const { issues, good } = doneNotes(r, lateAfter);
    for (const text of issues) chips.push([text, r.open_incidents.some((i) => i.type === text) ? "danger" : "warning"]);
    if (!issues.length) for (const text of good) chips.push([text, "success"]);
  } else for (const i of r.open_incidents) chips.push([i.type, "danger"]);

  const progress = progressOf(r.status);
  const showBar = segment === "assigned" || segment === "in_progress";
  const Icon = when.icon;
  return (
    <li className={styles.card} data-selected={selected} aria-label={`${r.delivery_number}, ${r.order.customer_name}`}>
      <button type="button" className={styles.cardButton} onClick={onSelect} aria-pressed={selected}>
        <span className={styles.cardTop}>
          <strong className={styles.code}>{r.delivery_number}</strong>
          {waiting ? (
            <span className={styles.when} data-tone={when.tone}>
              <Icon size={13} aria-hidden /> {when.text}
            </span>
          ) : (
            <span className={styles.badge} data-tone={r.status === "suspended" ? "danger" : tone}>
              {label}
            </span>
          )}
          <span className={styles.cardEnd} data-tone={waiting ? undefined : when.tone}>
            {waiting ? (
              litres ? (
                `${litres.toLocaleString("en-KE")} L`
              ) : null
            ) : (
              <>
                <Icon size={13} aria-hidden /> {when.text}
              </>
            )}
          </span>
        </span>
        <span className={styles.who}>{r.order.customer_name}</span>
        <span className={styles.small}>{[r.location?.label, r.geofence?.name].filter(Boolean).join(" · ")}</span>
        {!waiting && crew ? (
          <span className={styles.crew}>
            <Truck size={13} aria-hidden /> {crew}
          </span>
        ) : null}
        {note ? (
          <span className={styles.cardNote} data-tone={note[1]}>
            {note[0]}
          </span>
        ) : null}
        {chips.length ? (
          <span className={styles.chips}>
            {chips.map(([text, t]) => (
              <span key={text} className={styles.chip} data-tone={t}>
                {text}
              </span>
            ))}
          </span>
        ) : null}
        {showBar ? (
          <span className={styles.bar} role="img" aria-label={`Step ${progress.done} of ${progress.of}`}>
            {Array.from({ length: progress.of }, (_, i) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: the bar's segments are fixed
                key={i}
                data-on={i < progress.done || undefined}
                data-tone={r.status === "suspended" ? "danger" : alert ? "warning" : undefined}
              />
            ))}
          </span>
        ) : null}
      </button>
    </li>
  );
}
